import { Button } from '@synoptic/ui-kit/button/button.js';
import { Checkbox } from '@synoptic/ui-kit/checkbox/checkbox.tsx';
import { Input } from '@synoptic/ui-kit/input/input.tsx';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.ts';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from '@synoptic/ui-kit/select/select.tsx';
import { clsx } from 'clsx';
import { FC, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { StreamSubscriptionCancelReason } from '@/__generated__/graphql.ts';
import {
  cancelSubscriptionForm,
  checkbox,
  fieldBox,
  selectFormInfoTitle,
} from '@/components/streams/cancel-subscription/cancel-subscription.css.ts';
import {
  formInfoTitle,
  columnFields,
  streamFormActions,
  streamFormTrigger,
} from '../stream-form/stream-form.css.ts';

const reasonSelectOptions = Object.values(StreamSubscriptionCancelReason)
  .filter((value) => value !== StreamSubscriptionCancelReason.RemovedByAuthor)
  .map((value) => ({
    value,
    content: value
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/^\w/, (char) => char.toUpperCase()),
  }));

export type CancelSubscriptionFormInputs = {
  cancelationReason: StreamSubscriptionCancelReason;
  cancelationComment: string;
  refundComment?: string | undefined;
};

export type CancelSubscriptionFormProps = {
  onSubmit: (input: CancelSubscriptionFormInputs) => Promise<void>;
};
export const CancelSubscriptionForm: FC<CancelSubscriptionFormProps> = ({
  onSubmit,
}) => {
  const isTablet = useIsTablet();

  const { handleSubmit, formState, register, control } =
    useForm<CancelSubscriptionFormInputs>();
  const formRef = useRef<HTMLFormElement>(null);

  const [isRefund, setIsRefund] = useState(false);
  const reason = useWatch({ control, name: 'cancelationReason' });
  const showRefund =
    reason === StreamSubscriptionCancelReason.DissatisfiedWithQuality;

  const submit: SubmitHandler<CancelSubscriptionFormInputs> = async (
    data: CancelSubscriptionFormInputs,
  ) => {
    await onSubmit({
      cancelationReason: data.cancelationReason,
      cancelationComment: data.cancelationComment,
      refundComment: isRefund && showRefund ? data.refundComment : undefined,
    });
  };

  return (
    <form
      ref={formRef}
      className={clsx(columnFields, cancelSubscriptionForm)}
      onSubmit={handleSubmit(submit)}
    >
      <div className={fieldBox}>
        <h3 className={clsx(formInfoTitle, selectFormInfoTitle)}>
          Why are you cancelling your subscription?
        </h3>
        <Controller
          name={'cancelationReason'}
          control={control}
          rules={{ required: 'This field is required' }}
          render={({ field }) => (
            <Select
              defaultValue={field.value}
              onValueChange={(state) => {
                field.onChange(state);
              }}
            >
              <SelectTrigger
                placeholder={'Select'}
                errorMessage={formState.errors.cancelationReason?.message}
                className={streamFormTrigger}
                variant={'outline'}
              />
              <SelectContent position={'popper'} items={reasonSelectOptions} />
            </Select>
          )}
        />
      </div>

      {showRefund ? (
        <label className={checkbox}>
          <Checkbox
            checked={isRefund}
            onCheckedChange={(checked: boolean) => setIsRefund(checked)}
          />
          <span>I want a refund</span>
        </label>
      ) : null}

      {isRefund && showRefund ? (
        <div className={fieldBox}>
          <h3 className={formInfoTitle}>
            Please provide a brief explanation for your refund request:
          </h3>
          <Input
            {...register('refundComment', {
              required: 'This field is required',
              validate: (v) => v && v.trim().length > 0,
            })}
            errorMessage={formState.errors.refundComment?.message}
            placeholder={'Write the reason for refund'}
            label={'A reason for refund'}
          />
        </div>
      ) : null}

      {reason ? (
        <div className={fieldBox}>
          <h3 className={formInfoTitle}>Anything that can be improved?</h3>
          <Input
            {...register('cancelationComment', {
              required: 'This field is required',
              validate: (v) => v.trim().length > 0,
            })}
            errorMessage={formState.errors.cancelationComment?.message}
            placeholder={'Write your response'}
            label={'Tell us more'}
          />
        </div>
      ) : null}

      <div className={streamFormActions}>
        <Button
          loading={formState.isSubmitting}
          disabled={
            Boolean(formState.errors.cancelationReason) ||
            Boolean(formState.errors.cancelationComment) ||
            Boolean(formState.errors.refundComment)
          }
          type="submit"
          size={isTablet ? 'large' : 'medium'}
        >
          Next
        </Button>
      </div>
    </form>
  );
};
